import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsLoader } from '../../utils/loadGoogleMapsLoader';
import './style.scss';

type GoogleMapPinProps = {
  address: string;
  apiKey: string | undefined;
  zoom: number;
};

export const GoogleMapPin: React.FC<GoogleMapPinProps> = ({
  address,
  apiKey,
  zoom,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalidKey, setInvalidKey] = useState(true);

  useEffect(() => {
    if (!address) {
      setError('No shipping address provided.');
      return;
    }

    setError(null);

    const initializeMap = () => {
      if (!window.google || !mapRef.current) {
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;

          const map = new window.google.maps.Map(mapRef.current!, {
            center: location,
            zoom,
            disableDefaultUI: true,
          });

          new window.google.maps.Marker({ map, position: location });

          class CustomLabel extends window.google.maps.OverlayView {
            div: HTMLDivElement | null = null;

            onAdd() {
              this.div = document.createElement('div');
              this.div.className = 'google-map-label';

              const title = document.createElement('div');
              title.className = 'label-title';
              title.textContent = 'Shipping Address';

              const addressText = document.createElement('div');
              addressText.className = 'label-address';
              addressText.textContent = address;

              this.div.appendChild(title);
              this.div.appendChild(addressText);

              this.getPanes()?.floatPane.appendChild(this.div);
            }

            draw() {
              if (!this.div) return;
              const projection = this.getProjection();
              const point = projection.fromLatLngToDivPixel(location);
              if (point) {
                this.div.style.left = `${point.x}px`;
                this.div.style.top = `${point.y}px`;
              }
            }

            onRemove() {
              if (this.div?.parentNode) {
                this.div.parentNode.removeChild(this.div);
              }
              this.div = null;
            }
          }

          new CustomLabel().setMap(map);
        } else {
          setError('We couldn’t locate this address on the map.');
        }
      });
    };

    const loadAndInitMap = async () => {
      try {
        await loadGoogleMapsLoader(apiKey);
        setInvalidKey(false);
        requestAnimationFrame(() => initializeMap());
      } catch {
        setInvalidKey(true);
      }
    };

    loadAndInitMap();
  }, [address, apiKey, zoom]);

  if (invalidKey) return null;
  return (
    <div className="google-map-wrapper">
      <div ref={mapRef} className="google-map-container" />
      {error && <div className="google-map-error">{error}</div>}
    </div>
  );
};
